import { ORIENTATION } from '../../../cards/constants';

const AXIS = {
  X: 'X',
  Y: 'Y',
};

const orderFaceboxes = (faceboxes, axis) => {
  return [...faceboxes].sort((a, b) => {
    if (axis === AXIS.Y) {
      if (a[1] < b[1]) {
        return -1;
      }
      if (a[1] > b[1]) {
        return 1;
      }

      return 0;
    }

    if (a[0] < b[0]) {
      return -1;
    }
    if (a[0] > b[0]) {
      return 1;
    }

    return 0;
  });
};
/**
 * Given an array of faceboxes, generates a master facebox which encapsulates all of them
 *
 * @param {Array} faceboxes the faceboxes to get the master for
 * @returns {Array}
 */
export const getMasterFacebox = faceboxes => {
  if (faceboxes.length === 0) {
    return [];
  }
  // A facebox is composed by [x, y, width, height]
  const xOrdered = orderFaceboxes(faceboxes, AXIS.X);
  const yOrdered = orderFaceboxes(faceboxes, AXIS.Y);

  const lastItem = faceboxes.length - 1;
  return [
    xOrdered[0][0],
    yOrdered[0][1],
    xOrdered[lastItem][0] - xOrdered[0][0] + xOrdered[lastItem][2],
    yOrdered[lastItem][1] - yOrdered[0][1] + yOrdered[lastItem][3],
  ];
};

export const getFaceboxBasedFittedCrobox = (
  containerSize,
  scaleAxis,
  photoFaceboxes,
  fittedCropbox,
) => {
  const { width: containerWidth, height: containerHeight } = containerSize;
  const newFittedCropbox = { ...fittedCropbox };
  let isFitted = false;
  // Objectively, the photo's master facebox will be inside the margins
  // in, at the very least, 1 dimension (the one that is being scaled to)
  // therefore, we only need to worry about "shifting" the other position
  // such that "most" of the faces are centered on the other axis. Since
  // overall the container is the one determining the growth axis, use it
  // to determine how we will handle the faces.

  const orderedFaceboxes = orderFaceboxes(
    photoFaceboxes,
    scaleAxis === AXIS.X ? AXIS.Y : AXIS.X,
  );

  while (!isFitted && orderedFaceboxes.length > 0) {
    // Get the master facebox
    const masterFacebox = getMasterFacebox(orderedFaceboxes);
    //  Scale the facebox
    const { scale, x: minX, y: minY } = fittedCropbox;
    const scaledFacebox = masterFacebox.map(dim => {
      // In rare situations, a facebox might have a negative X or Y
      // index due to it being too close to the edge. Safeguard such
      // cases.
      if (dim < 0) {
        return 0;
      }
      return dim * scale;
    });
    //  Parse the scaled facebox sizes
    const [x, y, width, height] = scaledFacebox;
    const faceboxWidthInches = containerWidth * width;
    const faceboxHeightInches = containerHeight * height;

    // If the Facebox is already too big to fit in the growing dimension,
    // disregard one of the faces at a time until it fits. Each time, take
    // from a different side in order to "close in" to the center-most faces
    if (
      (scaleAxis === AXIS.Y && faceboxWidthInches > containerWidth) ||
      (scaleAxis === AXIS.X && faceboxHeightInches > containerHeight)
    ) {
      if (orderedFaceboxes.length % 2) {
        orderedFaceboxes.pop();
      } else {
        orderedFaceboxes.shift();
      }
      //  Facebox fits the container. Try centering it on the same axis.
    } else if (scaleAxis === AXIS.Y) {
      // Equals distance from the photo's center to the center of the facebox
      const xPosition = Math.abs(scale / 2 - (x + width / 2));
      // Determine if the photo should be moved to the t or right based off the
      // position of the faceboxes' middle.
      const movementDirection = scale / 2 > x + width / 2 ? 1 : -1;
      // The photo should move the minimum between the current position (which
      // would put it at the edge of the photo) and the facebox intended pos
      newFittedCropbox.x +=
        movementDirection * Math.min(Math.abs(minX), xPosition);
      isFitted = true;
    } else {
      const yPosition = Math.abs(scale / 2 - (y + height / 2));
      const movementDirection = scale / 2 > y + height / 2 ? 1 : -1;
      newFittedCropbox.y +=
        movementDirection * Math.min(Math.abs(minY), yPosition);
      isFitted = true;
    }
  }

  return newFittedCropbox;
};
/**
 * Calculates a fitted cropbox for a photo added to a container
 *
 * @param {Object} containerSize a generic container sizing (width, height)
 * @param {Number} [containerSize.width] the container width
 * @param {Number} [containerSize.height] the container height
 * @param {String} orientation either LANDSCAPE or PORTRAIT
 * @param {Object} photoThumbnail a thumbnail of the photo, including width & height
 * @param {Array} photoFaceboxes a set of faces belonging to the photo
 * @returns {{rotation: number, x: number, y: number, scale: number}}
 */
export const getFitPositionPhotoCropbox = (
  containerSize,
  orientation,
  photoThumbnail,
  photoFaceboxes = [],
) => {
  const baseCropbox = {
    x: 0,
    y: 0,
    scale: 1,
    rotation: 0,
  };

  if (!containerSize || !photoThumbnail) {
    return baseCropbox;
  }

  const { width: photoWidth, height: photoHeight } = photoThumbnail;
  const { width: containerWidth, height: containerHeight } = containerSize;

  const photoIsLandscape = photoWidth >= photoHeight;
  const orientationsMatch =
    (orientation === ORIENTATION.LANDSCAPE && photoIsLandscape) ||
    (orientation === ORIENTATION.PORTRAIT && !photoIsLandscape);

  // These values amount to the amount (%) we need to "increase" the lower
  // value to make it the same as the higher one. We need to calculate this
  // at both, photo & container level such that we can get a photo that "fills"
  // the screen when containerSizeRatio = 1 (photo level) and then is increased
  // to accommodate to the "biggest" side of the container (container level).
  const photoRatio =
    Math.max(photoWidth, photoHeight) / Math.min(photoWidth, photoHeight);

  const containerSizeRatio =
    Math.max(containerWidth, containerHeight) /
    Math.min(containerWidth, containerHeight);

  // If the ratios are the same, and the orientation
  // is the same, we don't need to increase anything.
  if (
    photoRatio === containerSizeRatio &&
    photoIsLandscape === (orientation === ORIENTATION.LANDSCAPE)
  ) {
    return baseCropbox;
  }

  let finalRatio;
  // If orientations match, we only need to increase by the necessary amount
  if (orientationsMatch) {
    finalRatio = 1 + Math.abs(photoRatio - containerSizeRatio);
    //  Otherwise, we need to account both increases as we did before.
  } else {
    finalRatio = containerSizeRatio * photoRatio;
  }

  // Add a bit of leeway to the scaling, ensuring the scaled photo covers
  // edge to edge while keeping extra space for adjusting the growing axis
  finalRatio += 0.01;

  // The scale increase would be ((FINAL - OG_SCALE) / OG_SCALE) but
  // since og scale is  1, we only subtract 1 from the final ratio.
  const scaleIncrease = Math.round((finalRatio - 1) * 1e2) / 1e2;

  const fittedCropbox = {
    ...baseCropbox,
    scale: Math.round(finalRatio * 1e2) / 1e2,
    // Moving by 50% of the increase centers the item based off the new scale
    x: Math.round(-(scaleIncrease / 2) * 1e3) / 1e3,
    y: Math.round(-(scaleIncrease / 2) * 1e3) / 1e3,
  };

  if (false && photoFaceboxes.length > 0) {
    // To determine the scale axis (i.e the axis that will "outgrow" the photo)
    // we must identify the axis where the photo will grow to it's 100%. This
    // will not necessarily match orientations, but will instead rely on the
    // space the photo has to grow within the container, combined with the actual
    // size of the photo. In short, this will be related to the photo ratio
    // and the container's ratio.
    let scaleAxis;

    if (photoRatio >= containerSizeRatio) {
      scaleAxis = photoIsLandscape ? AXIS.Y : AXIS.X;
    } else {
      scaleAxis = photoIsLandscape ? AXIS.X : AXIS.Y;
    }

    return getFaceboxBasedFittedCrobox(
      containerSize,
      scaleAxis,
      photoFaceboxes,
      fittedCropbox,
    );
  }

  return fittedCropbox;
};
